import {useSettings} from '@common/core/settings/use-settings';
import {useAppearanceEditorMode} from '@common/admin/appearance/commands/use-appearance-editor-mode';
import {useAuth} from '@common/auth/use-auth';
import {Route, Routes} from 'react-router-dom';
import {ToastContainer} from '@common/ui/toast/toast-container';
import {EmailVerificationPage} from '@common/auth/ui/email-verification-page/email-verification-page';
import {DialogStoreOutlet} from '@common/ui/overlays/store/dialog-store-outlet';
import {AppearanceListener} from '@common/admin/appearance/commands/appearance-listener';
import {CookieNotice} from '@common/ui/cookie-notice/cookie-notice';
import {AuthRoute} from '@common/auth/guards/auth-route';
import React, {Fragment, useCallback} from 'react';
import {FullPageLoader} from '@common/ui/progress/full-page-loader';
import {DynamicHomepage} from '@common/ui/dynamic-homepage';
import {LandingPage} from '@app/landing-page/landing-page';
import {AuthRoutes} from '@common/auth/auth-routes';
import {BillingRoutes} from '@common/billing/billing-routes';
import {NotificationRoutes} from '@common/notifications/notification-routes';
import {ContactUsPage} from '@common/contact/contact-us-page';
import {CustomPageLayout} from '@common/custom-page/custom-page-layout';
import {NotFoundPage} from '@common/ui/not-found-page/not-found-page';
import {useZainSdRedirect} from '@common/billing/checkout/zain-sd/use-zain-sd-redirect';
import {useSudaniRedirect} from '@common/billing/checkout/sudani-spay/use-sudani-redirect';
import {useXceedAutoLogin} from '@common/billing/checkout/use-xceed-auto-login';
import {useTrackingParameters} from '@common/utils/hooks/use-tracking-parameters';
import type {TrackingParameters} from '@common/utils/hooks/use-tracking-parameters';
import {apiClient} from '@common/http/query-client';
import {useCaptureTrialLink} from '@common/billing/trials/offer/use-trial-offer';

const AdminRoutes = React.lazy(() => import('@common/admin/admin-routes'));
const WebPlayerRoutes = React.lazy(
  () => import('@app/web-player/web-player-routes'),
);
const BackstageRoutes = React.lazy(
  () => import('@app/web-player/backstage/backstage-routes'),
);
const SwaggerApiDocs = React.lazy(
  () => import('@common/swagger/swagger-api-docs-page'),
);
const TryPage = React.lazy(
  () => import('@common/billing/trials/try-page/try-page'),
);

export function AppRoutes() {
  const {billing, notifications, require_email_confirmation, api, homepage} =
    useSettings();
  const {isAppearanceEditorActive} = useAppearanceEditorMode();
  const {user, hasPermission} = useAuth();
  const updateTrackingParameters = useCallback(
    (params: TrackingParameters) => {
      if (!user) return;
      apiClient.post('users/me/tracking-parameters', {
        tracking_parameters: params,
      });
    },
    [user],
  );
  useTrackingParameters({onCapture: updateTrackingParameters});
  // a free-trial code can arrive on any page as ?trial=CODE; it only
  // remembers the code and never touches the tracking above
  useCaptureTrialLink();
  useZainSdRedirect();
  const sudaniLoading = useSudaniRedirect();
  const xceedLoading = useXceedAutoLogin();
  const loading = xceedLoading || sudaniLoading;
  if (user != null && require_email_confirmation && !user.email_verified_at) {
    return (
      <Fragment>
        <ToastContainer />
        <Routes>
          <Route path="*" element={<EmailVerificationPage />} />
        </Routes>
        <DialogStoreOutlet />
      </Fragment>
    );
  }

  return (
    <Fragment>
      <AppearanceListener />
      <CookieNotice />
      <ToastContainer />
      {loading ? (
        <FullPageLoader
          screen
          className='!bg-white/60 absolute top-0 left-0 z-[900]'
        />
      ) : null}
      <Routes>
        <Route
          path="/*"
          element={
            <AuthRoute requireLogin={false} permission="music.view">
              <React.Suspense fallback={<FullPageLoader screen />}>
                <WebPlayerRoutes />
              </React.Suspense>
            </AuthRoute>
          }
        />

        <Route
          path="backstage/*"
          element={
            <AuthRoute>
              <React.Suspense fallback={<FullPageLoader screen />}>
                <BackstageRoutes />
              </React.Suspense>
            </AuthRoute>
          }
        />
        {!homepage?.type.startsWith('channel') &&
          (user == null || isAppearanceEditorActive) && (
            <Route
              path="/"
              element={
                <DynamicHomepage homepageResolver={() => <LandingPage />} />
              }
            />
          )}
        <Route
          path="/admin/*"
          element={
            <AuthRoute permission="admin.access">
              <React.Suspense fallback={<FullPageLoader screen />}>
                <AdminRoutes />
              </React.Suspense>
            </AuthRoute>
          }
        />
        {AuthRoutes}
        {billing.enable && BillingRoutes}
        {notifications.integrated && NotificationRoutes}
        {api?.integrated && hasPermission('api.access') && (
          <Route
            path="api-docs"
            element={
              <React.Suspense fallback={<FullPageLoader screen />}>
                <SwaggerApiDocs />
              </React.Suspense>
            }
          />
        )}
        <Route
          path="try"
          element={
            <React.Suspense fallback={<FullPageLoader screen />}>
              <TryPage />
            </React.Suspense>
          }
        />
        <Route path="contact" element={<ContactUsPage />} />
        <Route path="pages/:pageSlug" element={<CustomPageLayout />} />
        <Route path="pages/:pageId/:pageSlug" element={<CustomPageLayout />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      <DialogStoreOutlet />
    </Fragment>
  );
}
